import {
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer
} from "react"
import { useAlert } from "matsuri-ui"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useParams } from "react-router"
import { useCleaning } from "../../../hooks/useCleaning"
import { CleaningInfoDetailType } from "@/model/cleaningInfo"
import { assertIsDefined } from "@/utils/assetIsDefined"
import {
    callRequestCreateTrouble,
    callRequestFinish,
    callRequestStart,
    requestCreateTroubleFunc,
    requestCreateTroubleObject,
    uploadErrandPictureFunc,
    useUploadErrandPicture,
    uploadErrandPictureObject
} from "@/hooks/useCleaningInfos"
import { requestGetKeyboxState } from "@/hooks/useKeyboxNumber"
import { useTranslation } from "@/hooks/useTranslation"

export interface CleaningInfoReducerType {
    detail?: CleaningInfoDetailType
    cacheExpire?: number
}

export interface CleaningInfoContextType extends CleaningInfoReducerType {
    update: (data: CleaningInfoDetailType) => void
    requestStart: () => void
    requestFinish: () => void
    uploadErrandPicture: uploadErrandPictureFunc
    requestCreateTrouble: requestCreateTroubleFunc
}

const CleaningDetailContext = createContext({} as CleaningInfoContextType)

export const useCleaningDetail = () => {
    return useContext(CleaningDetailContext)
}

enum CleaningInfoReducerActionTypes {
    START_CLEANING = "START_CLEANING",
    FINISH_CLEANING = "FINISH_CLEANING",
    UPDATE = "UPDATE"
}
const cleaningReducer = (
    state: CleaningInfoReducerType,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    action: any
): CleaningInfoReducerType => {
    switch (action.type) {
        case CleaningInfoReducerActionTypes.START_CLEANING:
            return {
                detail: state.detail && { ...state.detail, status: "started" },
                cacheExpire: state.cacheExpire
            }
        case CleaningInfoReducerActionTypes.FINISH_CLEANING:
            return {
                detail: state.detail && { ...state.detail, status: "finished" },
                cacheExpire: state.cacheExpire
            }
        case CleaningInfoReducerActionTypes.UPDATE:
            return {
                detail: action.payload.data,
                cacheExpire: Date.now() + 3600
            }
        default:
            return { ...state }
    }
}

export const CleaningDetailProvider = ({ children }: PropsWithChildren) => {
    const { t } = useTranslation()
    const { throwAlert } = useAlert()
    const { token } = useAuth()
    const { cleaningId } = useParams<"cleaningId">()

    assertIsDefined(cleaningId)

    const [store, dispatch] = useReducer(cleaningReducer, {})

    const updateCleaningDetail = useCallback((data: CleaningInfoDetailType) => {
        dispatch({
            type: CleaningInfoReducerActionTypes.UPDATE,
            payload: {
                data,
                expire: Date.now() + 3600
            }
        })
    }, [])

    const { data: cleaningInfoDetailType, refetch: refetchCleaning } =
        useCleaning(token, cleaningId)
    useEffect(() => {
        if (cleaningInfoDetailType) {
            updateCleaningDetail(cleaningInfoDetailType)
        }
    }, [cleaningInfoDetailType, updateCleaningDetail])

    const requestStart = useCallback(async () => {
        const { shouldRotate, error } = await requestGetKeyboxState(
            token,
            cleaningId
        )
        if (shouldRotate) {
            window.alert(
                t(
                    "清掃を開始する前にキーボックスの番号変更を完了してください。"
                )
            )

            return
        }
        if (error) {
            const ok = window.confirm(
                t(
                    `次のエラーが発生しています。無視して続行しますか？: ${error}`
                )
            )
            if (!ok) {
                return
            }
        }

        const { error: startError } = await callRequestStart(token, cleaningId)
        if (startError) {
            throwAlert(startError, {
                errorMessage: t("開始に失敗しました"),
                ignoreSuccess: true
            })

            return
        }

        dispatch({ type: CleaningInfoReducerActionTypes.START_CLEANING })
    }, [cleaningId, token, t, throwAlert])

    const requestFinish = useCallback(() => {
        callRequestFinish(token, cleaningId)
        dispatch({ type: CleaningInfoReducerActionTypes.FINISH_CLEANING })
    }, [cleaningId, token])

    const requestUploadErrandPicture = useUploadErrandPicture()
    const uploadErrandPicture = useCallback(
        async (input: uploadErrandPictureObject) => {
            await requestUploadErrandPicture(input)
            await refetchCleaning()
        },
        [requestUploadErrandPicture, refetchCleaning]
    )

    const requestCreateTrouble = useCallback(
        async (input: requestCreateTroubleObject) => {
            await callRequestCreateTrouble(token, cleaningId, input)
        },
        [cleaningId, token]
    )

    const value = useMemo(
        () => ({
            detail: store.detail,
            cacheExpire: store.cacheExpire,
            update: updateCleaningDetail,
            requestStart,
            requestFinish,
            uploadErrandPicture,
            requestCreateTrouble
        }),
        [
            requestCreateTrouble,
            requestFinish,
            requestStart,
            store.cacheExpire,
            store.detail,
            updateCleaningDetail,
            uploadErrandPicture
        ]
    )

    return (
        <CleaningDetailContext.Provider value={value}>
            {children}
        </CleaningDetailContext.Provider>
    )
}
